import React, { useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { DataTable, type ColumnDef, type ActionDef } from "@/components/data-table/DataTable";
import { BlueButton } from "@/components/ui/Buttons";
import { usePlatformAPI } from "@/api/PlatformProvider";

type Partner = {
    id: string;
    name: string;
    description?: string;
    enabled: boolean;
    createdAt: string;
};

const LIMIT = 20;

const columns: ColumnDef<Partner>[] = [
    { key: "name", label: "Name", sortable: true, width: "1fr", required: true },
    { key: "description", label: "Description", width: "2fr" },
    {
        key: "enabled",
        label: "Status",
        width: "100px",
        render: (v) => (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${v ? "bg-desaturated-green text-dark-green" : "bg-desaturated-red text-dark-red"}`}>
                {v ? "Enabled" : "Disabled"}
            </span>
        ),
    },
    {
        key: "createdAt",
        label: "Created",
        sortable: true,
        width: "160px",
        render: (v) => new Date(v as string).toLocaleDateString(),
    },
];

function PartnersPage() {
    const api = usePlatformAPI();
    const navigate = useNavigate();
    const location = useLocation();

    const appsURL = (p: Partner) => {
        const from = encodeURIComponent(location.pathname + location.search);
        return `/partners/${p.id}?from=${from}`;
    };

    const getItems = useCallback(async (params: {
        search: string;
        page: number;
        limit: number;
        orderBy?: { field: string; direction: "asc" | "desc" };
    }) => {
        const query: Record<string, unknown> = { page: params.page, limit: params.limit };
        if (params.search) query.search = params.search;
        if (params.orderBy) {
            query.orderBy = params.orderBy.field;
            query.orderByDir = params.orderBy.direction;
        }
        const [data, err] = await api.get<{ items: Partner[]; total: number }>("api/partners", query);
        if (err) throw err;
        return { items: data.items, total: data.total };
    }, [api]);

    const actions: ActionDef<Partner>[] = [
        {
            label: "View",
            onClick: (p) => { navigate(appsURL(p)); },
        },
        {
            label: "Edit",
            onClick: (p) => { navigate(`/partners/${p.id}/edit`); },
        },
        {
            label: (p) => p.enabled ? "Disable" : "Enable",
            onClick: async (p) => {
                const [, err] = await api.post(`api/partners/${p.id}/toggle-enabled`);
                if (err) throw err;
            },
            optimisticUpdate: (p) => ({ ...p, enabled: !p.enabled }),
            confirm: {
                title: (p) => p.enabled ? `Disable "${p.name}"?` : `Enable "${p.name}"?`,
                description: (p) => p.enabled
                    ? "This will deactivate the partner and suspend their access. You can re-enable them at any time."
                    : "This will reactivate the partner and restore their access.",
                confirmLabel: "Confirm",
                variant: "warning",
            },
        },
        {
            label: "Delete",
            onClick: async (p) => {
                const [, err] = await api.delete(`api/partners/${p.id}`);
                if (err) throw err;
            },
            confirm: {
                title: (p) => `Delete "${p.name}"?`,
                description: "This will permanently delete the partner and all associated data. This action cannot be undone.",
                confirmLabel: "Delete",
                variant: "danger",
            },
        },
    ];

    return (
        <div className="flex flex-col gap-8">
            <div className="flex flex-row gap-8 items-center">
                <h1 className="text-xl font-semibold">Partners</h1>
                <div className="flex-1" />
                <div className="w-40">
                    <BlueButton text="Create Partner" onClick={() => navigate("/partners/create")} />
                </div>
            </div>
            <DataTable<Partner>
                id="partners"
                columns={columns}
                getItems={getItems}
                limit={LIMIT}
                actions={actions}
                onRowClick={(p) => navigate(appsURL(p))}
                syncWithUrl
            />
        </div>
    );
}

export default PartnersPage;
